import {
  DsDialogService,
  DsDialogRef,
  DS_DIALOG_DATA,
  DsDialogConfig,
} from 'src/app/shared/components/ds-dialog/ds-dialog.component';
import {
  Component,
  OnInit,
  ViewChild,
  ElementRef,
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  DestroyRef,
  inject,
} from '@angular/core';

import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { Subject, of } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { TranslateModule, TranslateService } from '@ngx-translate/core';
import {
  CommandItem,
  CommandCategory,
  COMMAND_REGISTRY,
  RecentItem,
  SearchResultItem,
  FederatedSearchResponse,
} from './command-palette.model';
import { trKey } from 'src/app/shared/utils/translate-safe';
import { NamespacedIconName } from 'src/app/shared/constants/material-icon-name';
import { UserService } from 'src/app/core/user/user.service';
import { defaultNavigation } from 'src/app/core/navigation/navigation';
import { NavigationItem } from 'src/app/core/navigation/navigation.model';
import { ReusableService } from 'src/app/services/reusable.service';
import { ApiEndpoint } from 'src/app/core/http/api';
import { IApiResponse } from 'src/app/core/http/api/api-types';
import {
  moduleSubNavConfig,
  SubNavItem,
} from 'src/app/layout/common/context-sidebar/module-sub-nav.config';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'app-command-palette',
  imports: [FormsModule, TranslateModule],
  templateUrl: './command-palette.component.html',
  styleUrl: './command-palette.component.scss',
})
export class CommandPaletteComponent implements OnInit, AfterViewInit {
  @ViewChild('searchInput') searchInput!: ElementRef<HTMLInputElement>;

  searchQuery = '';
  filteredItems: CommandItem[] = [];
  selectedIndex = 0;
  categories: {
    key: CommandCategory;
    label: string;
    translateKey: string;
    icon: NamespacedIconName;
  }[] = [
    {
      key: 'create',
      label: 'Quick Actions',
      translateKey: 'CommandPalette.QuickActions',
      icon: 'add_circle',
    },
    {
      key: 'navigation',
      label: 'Navigate',
      translateKey: 'CommandPalette.Navigate',
      icon: 'explore',
    },
    {
      key: 'report',
      label: 'Reports',
      translateKey: 'CommandPalette.Reports',
      icon: 'analytics',
    },
  ];

  private readonly RECENT_KEY = 'command-palette:recent';
  private readonly USAGE_KEY = 'command-palette:usage';
  private readonly MAX_RECENT = 5;

  recentItems: RecentItem[] = [];
  popularCommands: CommandItem[] = [];

  /** Kết quả tìm THỰC THỂ THẬT (đơn hàng/HBL/container...) — nguồn: FederatedSearch API. */
  entityResults: SearchResultItem[] = [];
  loadingEntities = false;

  private readonly _userService = inject(UserService);
  private readonly _reusableService = inject(ReusableService);
  private readonly _destroyRef = inject(DestroyRef);
  private readonly _cdr = inject(ChangeDetectorRef);
  private readonly _entitySearch$ = new Subject<string>();

  /**
   * RBAC-COMPLETE-01 W3 (23/08): palette phải khớp với module rail + route guard — trước đó 30
   * mục hiện với MỌI người, gồm cả link thẳng /administration/user cho tài khoản nghiệp vụ.
   * Suy module từ SEGMENT ĐẦU của route đối chiếu `defaultNavigation` (id = mã resource cấp
   * module đã gieo ở script 09) thay vì gắn tay mã cho từng mục — mục thêm sau tự được gate.
   */
  private readonly _moduleBySegment = new Map<string, string>(
    defaultNavigation
      .flatMap((group) => group.children ?? [])
      .filter((item) => !!item.link && !!item.id)
      .map((item) => [item.link!.split('/')[1], item.id!]),
  );

  /**
   * Hai module HUB mà ROUTE cố ý không gate (đồng bộ với app.routes.ts): /profile là
   * self-service, /knowledge là quyền nền mọi vai trò đều có (Read KNOWLEDGE trong khối
   * baseline của RoleGrantMatrix). Icon rail của chúng vẫn admin-only theo chốt Alex 23/08.
   */
  private static readonly PUBLIC_ROUTE_MODULES = new Set(['PROFILE', 'KB']);

  private isRouteAllowed(route?: string): boolean {
    if (!route) return true;
    const moduleId = this._moduleBySegment.get(route.split('/')[1]);
    if (!moduleId) return true;
    if (CommandPaletteComponent.PUBLIC_ROUTE_MODULES.has(moduleId)) return true;
    return this._userService.hasResource(moduleId); // isAdmin đã được bypass bên trong
  }

  /** Registry đã lọc theo quyền của phiên — nguồn duy nhất cho mọi danh sách bên dưới. */
  private allowedRegistry: CommandItem[] = [];

  constructor(
    private dialogRef: DsDialogRef<CommandPaletteComponent>,
    private router: Router,
    private _translateService: TranslateService,
  ) {}

  /**
   * Trước 30/08: mục "Điều hướng" chỉ đến từ 29 mục viết tay trong `COMMAND_REGISTRY` — vì
   * vậy gõ "house" không ra House Bill mà ra "Inventory Status" (khớp từ khoá "warehouse").
   * Duyệt phẳng TOÀN BỘ `defaultNavigation` (nguồn thật của thanh bên, ~39-41 route) làm
   * nguồn điều hướng chính; registry tĩnh vẫn giữ để giữ keyword/label tay đã tinh chỉnh.
   */
  private buildNavigationCommandItems(): CommandItem[] {
    const flatten = (items: NavigationItem[]): CommandItem[] =>
      items.flatMap((item) => {
        const own: CommandItem[] =
          item.link && item.title
            ? [
                {
                  id: `nav-route-${item.id ?? item.link}`,
                  label: trKey(this._translateService, item.title),
                  category: 'navigation',
                  icon: item.materialIcon ?? item.icon ?? 'explore',
                  route: item.link,
                },
              ]
            : [];
        return [...own, ...flatten(item.children ?? [])];
      });

    return flatten(defaultNavigation).filter((navItem) => this.isRouteAllowed(navItem.route));
  }

  /**
   * Bảng lệnh khi ô tìm TRỐNG (module-rail.jsx :: Palette, `q = ''`): hai nhóm "Tác vụ nhanh" +
   * "Điều hướng" của MODULE ĐANG MỞ.
   *
   * - Điều hướng = đúng các mục sidebar của module (`moduleSubNavConfig`, nguồn của context
   *   sidebar), lọc như sidebar lọc: `resourceId` qua `hasResource`, mục `planned` bỏ, và
   *   `isRouteAllowed` cho cổng cấp module. Không có danh sách cắm cứng nào.
   * - Tác vụ nhanh = mục `create` của registry mà route là `<mục điều hướng ở trên>/create` —
   *   tức một màn TẠO có thật, nằm dưới một mục người dùng đang được thấy. Mục `create` trỏ vào
   *   màn danh sách (Create Invoice → /sales/invoice) KHÔNG phải tác vụ nên không vào nhóm này.
   *
   * Module không có sidebar (vd trang chủ) ⇒ mảng rỗng ⇒ rơi về gần đây / phổ biến như cũ.
   */
  private buildEmptyStateItems(): CommandItem[] {
    const url = this.router.url.split('?')[0].split('#')[0];
    const first = url.split('/').filter(Boolean)[0];
    const subNav: SubNavItem[] = first ? (moduleSubNavConfig[`/${first}`] ?? []) : [];

    let section = '';
    const nav: CommandItem[] = [];
    for (const item of subNav) {
      if (item.section) section = trKey(this._translateService, item.section);
      if (item.status === 'planned') continue;
      if (item.resourceId && !this._userService.hasResource(item.resourceId)) continue;
      if (!this.isRouteAllowed(item.path)) continue;
      nav.push({
        id: `nav-sub-${item.path}`,
        label: trKey(this._translateService, item.label),
        category: 'navigation',
        icon: CommandPaletteComponent.fontGlyph(item.icon),
        route: item.path,
        hint: section || undefined,
      });
    }

    const quick: CommandItem[] = [];
    for (const navItem of nav) {
      const create = this.allowedRegistry.find(
        (c) => c.category === 'create' && c.route === `${navItem.route}/create`,
      );
      if (!create) continue;
      quick.push({
        ...create,
        label: this._translateService.instant('CommandPalette.CreateNew', { name: navItem.label }),
      });
    }
    return [...quick, ...nav];
  }

  /** Icon sidebar là khoá sprite (tiền tố namespace + dấu hai chấm); bảng lệnh vẽ bằng font ⇒ bỏ tiền tố. */
  private static fontGlyph(icon: NamespacedIconName | undefined): NamespacedIconName {
    if (!icon) return 'explore';
    const i = icon.indexOf(':');
    return (i === -1 ? icon : icon.slice(i + 1)) as NamespacedIconName;
  }

  /** Mục của trạng thái ô-tìm-trống — tính một lần lúc mở (route không đổi khi bảng đang mở). */
  private emptyStateItems: CommandItem[] = [];

  /** Thứ tự nhóm khi vẽ — `filteredItems` xếp theo đúng thứ tự này để phím ↑↓ đi đúng dòng. */
  private sortByCategory(items: CommandItem[]): CommandItem[] {
    const order = this.categories.map((c) => c.key);
    return [...items].sort((a, b) => order.indexOf(a.category) - order.indexOf(b.category));
  }

  ngOnInit(): void {
    const staticAllowed = COMMAND_REGISTRY.filter((item) => this.isRouteAllowed(item.route));
    const staticRoutes = new Set(staticAllowed.map((item) => item.route).filter(Boolean));
    const navItems = this.buildNavigationCommandItems().filter(
      (navItem) => !staticRoutes.has(navItem.route),
    );
    this.allowedRegistry = [...staticAllowed, ...navItems];

    this.loadRecentItems();
    // Mục "gần đây" lưu route thô trong localStorage — phiên trước có thể là tài khoản khác
    // với quyền rộng hơn; lọc lại để không mời người dùng bấm vào một cú 403.
    this.recentItems = this.recentItems.filter((item) => this.isRouteAllowed(item.route));
    this.loadPopularCommands();
    this.emptyStateItems = this.buildEmptyStateItems();
    this.filteredItems = this.emptyStateItems;

    this._entitySearch$
      .pipe(
        debounceTime(250),
        distinctUntilChanged(),
        switchMap((q) => {
          if (q.length < 2) {
            this.loadingEntities = false;
            this._cdr.markForCheck();
            return of<SearchResultItem[]>([]);
          }
          this.loadingEntities = true;
          this._cdr.markForCheck();
          return this._reusableService
            .postTyped<FederatedSearchResponse>(ApiEndpoint.SearchFederated, {
              searchQuery: q,
              maxResults: 5,
              includeEntities: true,
              includeRecent: false,
            })
            .pipe(
              switchMap((res: IApiResponse<FederatedSearchResponse>) =>
                of(res.result?.entityResults ?? []),
              ),
            );
        }),
        takeUntilDestroyed(this._destroyRef),
      )
      .subscribe({
        next: (items) => {
          this.entityResults = items;
          this.loadingEntities = false;
          this._cdr.markForCheck();
        },
        error: () => {
          this.loadingEntities = false;
          this._cdr.markForCheck();
        },
      });
  }

  ngAfterViewInit(): void {
    setTimeout(() => this.searchInput?.nativeElement?.focus(), 100);
  }

  onSearch(): void {
    const q = this.searchQuery.toLowerCase().trim();
    if (!q) {
      this.filteredItems = this.emptyStateItems;
      this.entityResults = [];
      this.loadingEntities = false;
    } else {
      this.filteredItems = this.sortByCategory(
        this.allowedRegistry
          .filter(
            (item) =>
              item.label.toLowerCase().includes(q) ||
              item.keywords?.some((kw) => kw.toLowerCase().includes(q)) ||
              item.category.includes(q),
          )
          .slice(0, 15),
      );
    }
    this.selectedIndex = 0;
    this._entitySearch$.next(q);
  }

  /** Nhóm kết quả thực thể theo `entityType` để render thành từng khối trong template. */
  get groupedEntityResults(): { entityType: string; items: SearchResultItem[] }[] {
    const groups = new Map<string, SearchResultItem[]>();
    for (const item of this.entityResults) {
      const list = groups.get(item.entityType) ?? [];
      list.push(item);
      groups.set(item.entityType, list);
    }
    return Array.from(groups.entries()).map(([entityType, items]) => ({ entityType, items }));
  }

  navigateToEntityResult(item: SearchResultItem): void {
    this.navigateTo(item.navigationPath);
  }

  /** Gần đây / phổ biến — chỉ khi ô trống VÀ module hiện tại không có mục nào để gợi ý. */
  get showSuggestions(): boolean {
    return !this.searchQuery.trim() && this.emptyStateItems.length === 0;
  }

  onKeydown(event: KeyboardEvent): void {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      this.selectedIndex = Math.min(this.selectedIndex + 1, this.filteredItems.length - 1);
      this.revealActive();
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      this.selectedIndex = Math.max(this.selectedIndex - 1, 0);
      this.revealActive();
    } else if (event.key === 'Enter' && this.filteredItems.length > 0) {
      event.preventDefault();
      this.executeItem(this.filteredItems[this.selectedIndex]);
    } else if (event.key === 'Escape') {
      this.dialogRef.close();
    }
  }

  /** Giữ dòng đang chọn trong vùng nhìn khi đi bằng phím trong danh sách dài. */
  private revealActive(): void {
    this._cdr.detectChanges();
    const host = this.searchInput?.nativeElement?.closest('.command-palette');
    const active = host?.querySelector<HTMLElement>('.command-palette__item--active');
    active?.scrollIntoView?.({ block: 'nearest' });
  }

  executeItem(item: CommandItem): void {
    this.trackUsage(item);
    this.addToRecent(item);
    this.dialogRef.close();
    if (item.route) {
      this.router.navigate([item.route]);
    }
  }

  navigateTo(route: string): void {
    this.dialogRef.close();
    this.router.navigate([route]);
  }

  getItemsByCategory(category: CommandCategory): CommandItem[] {
    return this.filteredItems.filter((i) => i.category === category);
  }

  getCategoryIcon(category: string): string {
    return this.categories.find((c) => c.key === category)?.icon || 'folder';
  }

  getCategoryLabel(category: string): string {
    const cat = this.categories.find((c) => c.key === category);
    return cat ? trKey(this._translateService, cat.translateKey) : category;
  }

  get activeCategories(): CommandCategory[] {
    const cats = new Set(this.filteredItems.map((i) => i.category));
    return this.categories.filter((c) => cats.has(c.key)).map((c) => c.key);
  }

  // ---------------------------------------------------------------------------
  // RECENT ITEMS
  // ---------------------------------------------------------------------------
  private loadRecentItems(): void {
    try {
      const stored = localStorage.getItem(this.RECENT_KEY);
      this.recentItems = stored ? JSON.parse(stored) : [];
    } catch {
      this.recentItems = [];
    }
  }

  private addToRecent(item: CommandItem): void {
    if (!item.route) return;

    const entry: RecentItem = {
      label: item.label,
      route: item.route,
      icon: item.icon,
      timestamp: Date.now(),
    };

    // Remove existing entry for the same route, then prepend
    this.recentItems = [entry, ...this.recentItems.filter((r) => r.route !== item.route)].slice(
      0,
      this.MAX_RECENT,
    );

    try {
      localStorage.setItem(this.RECENT_KEY, JSON.stringify(this.recentItems));
    } catch {
      /* quota exceeded — silently ignore */
    }
  }

  // ---------------------------------------------------------------------------
  // USAGE TRACKING / POPULAR COMMANDS
  // ---------------------------------------------------------------------------
  private loadPopularCommands(): void {
    const usageMap = this.getUsageMap();
    const entries = Object.entries(usageMap)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5);

    this.popularCommands = entries
      .map(([id, count]) => {
        // allowedRegistry (không phải COMMAND_REGISTRY): lịch sử dùng của phiên/tài khoản trước
        // không được hồi sinh một mục mà tài khoản hiện tại không có quyền.
        const cmd = this.allowedRegistry.find((c) => c.id === id);
        return cmd ? { ...cmd, usageCount: count } : null;
      })
      .filter((c) => c !== null) as CommandItem[];
  }

  private trackUsage(item: CommandItem): void {
    const usageMap = this.getUsageMap();
    usageMap[item.id] = (usageMap[item.id] || 0) + 1;

    try {
      localStorage.setItem(this.USAGE_KEY, JSON.stringify(usageMap));
    } catch {
      /* quota exceeded — silently ignore */
    }

    this.loadPopularCommands();
  }

  private getUsageMap(): Record<string, number> {
    try {
      const stored = localStorage.getItem(this.USAGE_KEY);
      return stored ? JSON.parse(stored) : {};
    } catch {
      return {};
    }
  }
}
